/**
 * Keyboard — on-screen Turkish keyboard with colored letter states.
 */
import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Key from './Key';
import { KEYBOARD_ROWS, GAME_STATUS } from '../../utils/constants';
import { addLetter, removeLetter, submitGuess } from '../../store/slices/gameSlice';
import { toTurkishUpper, isTurkishLetter } from '../../utils/gameLogic';

const Keyboard = () => {
  const dispatch = useDispatch();
  const letterStates = useSelector((state) => state.keyboard.letterStates);
  const gameStatus = useSelector((state) => state.game.status);

  const handleKey = useCallback(
    (key) => {
      if (gameStatus !== GAME_STATUS.PLAYING) return;

      if (key === 'ENTER') {
        dispatch(submitGuess());
      } else if (key === '⌫') {
        dispatch(removeLetter());
      } else {
        dispatch(addLetter(key));
      }
    },
    [dispatch, gameStatus]
  );

  // Physical keyboard listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameStatus !== GAME_STATUS.PLAYING) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        dispatch(submitGuess());
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        dispatch(removeLetter());
        return;
      }

      const upper = toTurkishUpper(e.key);
      if (upper.length === 1 && isTurkishLetter(upper)) {
        e.preventDefault();
        dispatch(addLetter(upper));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, gameStatus]);

  return (
    <div className="flex flex-col items-center gap-1.5 px-1 pb-2">
      {KEYBOARD_ROWS.map((row, rowIdx) => (
        <div key={rowIdx} className="flex gap-1 sm:gap-1.5 justify-center">
          {row.map((key) => (
            <Key
              key={key}
              value={key}
              onClick={handleKey}
              state={letterStates[key] || 'unused'}
              wide={key === 'ENTER' || key === '⌫'}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default Keyboard;
